import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Activity, CheckCircle, AlertCircle, TrendingDown } from "lucide-react";

/**
 * Compact AI Health widget for the Admin dashboard.
 * Polls /api/admin/ai-health/metrics every 60s and renders:
 *  - Status pill (Healthy / Degraded / Down)
 *  - 24-hour mini bar chart (fallbacks per hour)
 *  - Total counters
 */
const AIHealthWidget = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const { data } = await authAxios.get("/api/admin/ai-health/metrics?hours=24");
      setData(data);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 60_000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <Card data-testid="ai-health-widget">
        <CardContent className="py-6 text-center text-sm text-gray-500">Loading AI health…</CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const statusMap = {
    healthy: { label: "Healthy", color: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle, tint: "#10b981" },
    degraded: { label: "Degraded", color: "bg-yellow-100 text-yellow-700 border-yellow-300", icon: AlertCircle, tint: "#f59e0b" },
    down: { label: "AI Down", color: "bg-red-100 text-red-700 border-red-300", icon: TrendingDown, tint: "#ef4444" },
  };
  const S = statusMap[data.status] || statusMap.healthy;
  const StatusIcon = S.icon;

  const maxVal = Math.max(1, ...data.buckets.map(b => b.fallbacks + b.hard_fails));

  return (
    <Card data-testid="ai-health-widget" className="border-l-4" style={{ borderLeftColor: S.tint }}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity size={16} className="text-[#DC283C]" />
              AI Image Generator Health
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">Last 24 hours · auto-refresh 60s</p>
          </div>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-bold ${S.color}`}>
            <StatusIcon size={12} /> {S.label}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mini bar chart */}
        <div className="flex items-end gap-0.5 h-20 px-1" aria-label="Hourly fallback chart">
          {data.buckets.map((b, i) => {
            const total = b.fallbacks + b.hard_fails + (b.tier2 || 0);
            const hPct = total === 0 ? 4 : Math.max(6, (total / maxVal) * 100);
            const isNonZero = total > 0;
            const color = b.hard_fails > 0 ? "#ef4444" : b.fallbacks > 0 ? "#f59e0b" : b.tier2 > 0 ? "#8b5cf6" : "#e5e7eb";
            return (
              <div
                key={b.hour || i}
                className="flex-1 rounded-t transition-all hover:opacity-80"
                style={{ height: `${hPct}%`, backgroundColor: color, minHeight: 3 }}
                title={`${b.hour} — Gemini fails: ${b.tier2 || 0} (OpenAI saved), Reference fallbacks: ${b.fallbacks}, Hard fails: ${b.hard_fails}`}
              >
                {isNonZero && (
                  <div className="w-full h-full relative">
                    <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-gray-600">{total}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-1">
          <span>{data.buckets[0]?.hour}</span>
          <span className="text-center">24h</span>
          <span>now</span>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t">
          <div className="text-center">
            <p className="text-lg font-bold text-purple-600" data-testid="ai-tier2-count">{data.total_tier2 || 0}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">Tier-2<br/>(OpenAI)</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-[#1a1a2e]" data-testid="ai-total-fallbacks">{data.total_fallbacks}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">Tier-3<br/>(Gallery)</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-red-600" data-testid="ai-hard-fails">{data.total_hard_fails}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">Hard<br/>Fails</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-700" data-testid="ai-peak-hour">{data.peak?.hour || "—"}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">Peak<br/>Hour</p>
          </div>
        </div>

        <p className="text-[11px] text-gray-500 mt-3 italic">
          Purple = Gemini failed, OpenAI saved it. Yellow = both AI down, reference image served. Red = all 3 tiers failed.
        </p>
      </CardContent>
    </Card>
  );
};

export default AIHealthWidget;
